import get from 'lodash/get';
import { transformSearchEdges, FlattenedSearchResult } from './transformSearchEdges';

// This class handles search response processing and pagination for the Search component.
// It transforms GraphQL responses into flattened objects and manages pagination state.

class SearchResponseHandler {
  /**
   * Processes a GraphQL search response and returns flattened search results so we don't have to drill into node properties
   */
  static processSearchResponse(payload: any): FlattenedSearchResult[] {
    // Check for filter by job response structure first
    const resultsFilteredByJob = get(payload, 'data.sitterSearchByJob.results.edges');
    if (resultsFilteredByJob) {
      return transformSearchEdges(resultsFilteredByJob);
    }
    // Fall back to standard search response structure
    const edges = get(payload, 'data.sitterSearch.edges');
    return transformSearchEdges(edges);
  }

  /**
   * Handles pagination by updating the sitters array
   */
  private static handlePaginationUpdate(component: any, payload: any, append: boolean = false): void {
    const newSitters = this.processSearchResponse(payload);
    component.sitters = append ? component.sitters.concat(newSitters) : newSitters;
  }

  /**
   * Updates component metadata after processing search results
   */
  private static updateComponentMetadata(component: any, payload: any): void {
    component.searchResults = get(payload, 'data.sitterSearchByJob.results') || get(payload, 'data.sitterSearch');

    if (component.searchResultReceived) {
      component.searchResultReceived(payload);
    }

    // Display loading state for minimum 1 second to ensure visual feedback
    setTimeout(() => {
      component.loading = false;
    }, 1000);
  }

  /**
   * Main method to update search results with pagination support
   */
  static updateSearchResults(component: any, payload: any, isPagination = false): void {
    try {
      this.handlePaginationUpdate(component, payload, isPagination);
      this.updateComponentMetadata(component, payload);
    } catch (error) {
      console.error('Error processing search response:', error);
      if (component.onError) {
        component.onError(error);
      }
    }
  }

  /**
   * Updates backup search results from job-based search
   */
  static updateBackupResults(component: any, payload: any): void {
    try {
      const backupData = get(payload, 'data.sitterSearchByJob.backup.results');
      const backupEdges = get(backupData, 'edges');

      if (backupEdges) {
        const newSitters = transformSearchEdges(backupEdges);
        const existingSitters = component.backupResults.sitters ?? [];

        component.backupResults = {
          sitters: existingSitters.concat(newSitters),
          pageInfo: backupData.pageInfo,
        };
      }

      component.loadingBackup = false;
    } catch (error) {
      console.error('Error processing backup search response:', error);
      component.loadingBackup = false;
      if (component.onError) {
        component.onError(error);
      }
    }
  }
}

export default SearchResponseHandler;
